import { colors } from '@constants/colors';
import { EventRole, IEvent } from '@interfaces/event';
import { IRoom } from '@interfaces/rooms';
import { IUser } from '@interfaces/user';

import { isScheduledEvent } from './events';
import ParticipantScopes from '@constants/scopes';

import {
  clientSpecificUserEventPermissions,
  clientSpecificMultiRoleModal,
  clientSpecificUserEventRole,
  clientSpecificAvaturnCarousel,
  clientSpecificFeedbackModal,
  clientSpecificLastLoadingStepIcon,
  clientSpecificSEPVisibilityCarouselEvents,
  clientSpecificFirstTimeSDCarouselEvents,
  clientSpecificSDTooltipTypeCarouselEvents,
  clientSpecificEventFullLoadingPage,
  clientSpecificAppLoader,
  clientSpecificDashboardExtension,
  clientSpecificMandatoryLogoutModal,
} from '@clients/ek/helpers/clientSpecificUtils';

import { env } from 'env';
import ROUTES from '@constants/routes';

const {
  REACT_APP_CLIENT_ENV,
  REACT_APP_MULTI_PLAYER_TAG: REACT_APP_MULTI_PLAYER_TAG_FROM_ENV,
} = env;

const REACT_APP_MULTI_PLAYER_TAG =
  REACT_APP_MULTI_PLAYER_TAG_FROM_ENV || 'MultiPlayer';

export const getClientSpecificLastLoadingStepIcon = () => {
  let returnedIcon;
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      returnedIcon = clientSpecificLastLoadingStepIcon();
      break;
    default:
      null;
  }

  return returnedIcon;
};

export const getClientSpecificNotificationTheme = () => {
  let returnedThemeData;
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
    default:
      returnedThemeData = {
        background: '#03030399',
        color: colors.white,
      };
      break;
  }

  return returnedThemeData;
};

export const getClientSpecificFullScreenHelperVisible = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return false;
    default:
      return true;
  }
};

export const getClientSpecificLoadingExperienceSpeed = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return 0.75;
    default:
      return 1;
  }
};

const sortSEPEvents = (events: IEvent[]) => {
  if (events.length <= 1) return events;

  const filteredEvents = [...events];

  const sepAssessmentIndex = filteredEvents.findIndex((x) =>
    x.eventTags?.includes('SEP Assessment'),
  );

  if (sepAssessmentIndex > -1) {
    const event = filteredEvents[sepAssessmentIndex];

    filteredEvents.splice(sepAssessmentIndex, 1);
    filteredEvents.unshift(event);

    return filteredEvents;
  }

  return events;
};

export const getClientSpecificSortedCarouselEvents = (events: IEvent[]) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return sortSEPEvents(events);
    default:
      return events;
  }
};

export const getClientSpecificFirstTimeSDCarouselEvents = (
  events: (IEvent & { state: string | null | undefined })[],
  user: IUser,
): (IEvent & { state: string | null | undefined })[] => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificFirstTimeSDCarouselEvents({ events, user });
    }
    default:
      return events;
  }
};

export const getClientSpecificSDTooltipTypeCarouselEvents = (
  events: IEvent[],
  user: IUser,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificSDTooltipTypeCarouselEvents({ user, events });
    }
    default:
      return events;
  }
};

export const getClientSpecificSEPVisibilityCarouselEvents = (
  events: (IEvent & { inviteStartTime: number })[],
  user: IUser,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificSEPVisibilityCarouselEvents({ user, events });
    }
    default:
      return events;
  }
};

export const getClientSpecificSDCarouselEventsTransform = (
  events: (IEvent & {
    inviteStartTime: number;
    state: string | null | undefined;
  })[],
  user: IUser,
) => {
  getClientSpecificSEPVisibilityCarouselEvents(events, user);
  getClientSpecificSDTooltipTypeCarouselEvents(events, user);
  getClientSpecificFirstTimeSDCarouselEvents(events, user);
  getClientSpecificSortedCarouselEvents(events);

  return events;
};

export const getClientSpecificFeedbackModal = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return clientSpecificFeedbackModal();
    default:
      return null;
  }
};

export const getClientSpecificMultiRoleModal = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return clientSpecificMultiRoleModal();
    default:
      return null;
  }
};

export const getClientSpecificDashboardExtension = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return clientSpecificDashboardExtension();
    default:
      return null;
  }
};

export const getClientSpecificCanShareWebCameraOnScreen = (
  currentRoom: IRoom | null,
  currentEvent: IEvent | undefined,
  permissions?: ParticipantScopes[],
) => {
  const isShareCameraEnable = permissions?.includes(
    ParticipantScopes.HubEventShareCamera,
  );

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const tags =
        currentEvent && Array.isArray(currentEvent.eventTags)
          ? currentEvent.eventTags
          : [];

      if (tags.includes(REACT_APP_MULTI_PLAYER_TAG)) {
        return isShareCameraEnable;
      }

      return !!currentRoom?.isMultiPlayer && isShareCameraEnable;
    }
    default:
      return !!currentRoom?.isMultiPlayer && isShareCameraEnable;
  }
};

export const getClientSpecificCanShareScreenOnScreen = (
  currentRoom: IRoom | null,
  currentEvent: IEvent | undefined,
  permissions?: ParticipantScopes[],
) => {
  const isShareScreenEnable = permissions?.includes(
    ParticipantScopes.HubEventShareScreen,
  );

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const tags =
        currentEvent && Array.isArray(currentEvent.eventTags)
          ? currentEvent.eventTags
          : [];

      if (tags.includes(REACT_APP_MULTI_PLAYER_TAG)) {
        return isShareScreenEnable;
      }

      return !!currentRoom?.isMultiPlayer && isShareScreenEnable;
    }
    default:
      return !!currentRoom?.isMultiPlayer && isShareScreenEnable;
  }
};

export const getClientSpecificCanShowWebCameraOnAvatar = (
  currentRoom: IRoom | null,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return !!currentRoom?.isMultiPlayer;
    }
    default:
      return !!currentRoom?.isMultiPlayer;
  }
};

export const getClientSpecificCanSpeak = (currentRoom: IRoom | null) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return !!currentRoom?.isMultiPlayer;
    }
    default:
      return !!currentRoom?.isMultiPlayer;
  }
};

export const getClientSpecificAvaturnCarousel = (): string[] => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return clientSpecificAvaturnCarousel();
    default:
      return [];
  }
};

export const getClientSpecificShowNotificationSuccessfulQrCodeScanning =
  (): boolean => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return false;
      default:
        return true;
    }
  };

export const getClientSpecificIsLogoClickable = (): boolean => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return false;
    default:
      return true;
  }
};

export const getClientSpecificCanTeleport = (
  currentEvent: IEvent | undefined,
) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (currentEvent && isScheduledEvent(currentEvent)) {
        return {
          canTeleport: false,
          notificationMessage: 'teleportUnavailable',
        };
      } else {
        return { canTeleport: true };
      }
    }
    default:
      return { canTeleport: true };
  }
};

export const getClientSpecificUserEventPermissions = (props: {
  role?: EventRole;
  roleScopes?: { roleName: string; permissions: ParticipantScopes[] }[];
}) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificUserEventPermissions(props);
    }
    default:
      return [];
  }
};

export const getClientSpecificUserEventRole = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificUserEventRole();
    }
    default:
      return null;
  }
};

export const getClientSpecificAppLoader = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificAppLoader();
    }
    default:
      return null;
  }
};

export const getClientSpecificEventFullLoadingPage = () => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificEventFullLoadingPage();
    }
    default:
      return null;
  }
};

export const getClientSpecificMandatoryLogoutModal = (events: IEvent[]) => {
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificMandatoryLogoutModal(events);
    }
    default:
      return {
        onCancel: () => {
          window.location.assign(ROUTES.SELECT_EVENT);
        },
      };
  }
};
